/**
 * Client Programs page (`/my-coaching/clients/programs`): which statuses each tab
 * shows, which tab opens first, and the account-wide tile figures.
 */
import { EngagementStatus } from "~/lib/schemas/db-enums";
import {
  computeAttentionSignals,
  type AttentionInput,
} from "~/lib/shared/engagement-attention";

export type ClientProgramTab = "current" | "past" | "all";

type ClientProgramPhase = Exclude<ClientProgramTab, "all">;

const CLIENT_PROGRAM_TABS: readonly ClientProgramTab[] = [
  "current",
  "past",
  "all",
];

const PHASE_BY_STATUS = {
  [EngagementStatus.ACTIVE]: "current",
  [EngagementStatus.PAUSED]: "current",
  [EngagementStatus.FINISHED]: "past",
  [EngagementStatus.ARCHIVED]: "past",
  [EngagementStatus.CANCELLED]: "past",
} as const satisfies Record<EngagementStatus, ClientProgramPhase>;

const ALL_STATUSES = Object.values(EngagementStatus);

/** The tab in the address (`?tab=`); anything else means "no tab chosen". */
export function parseClientProgramTab(
  value: unknown,
): ClientProgramTab | undefined {
  return CLIENT_PROGRAM_TABS.find((tab) => tab === value);
}

/** The tab a program is listed under: Past once it is finished, archived or cancelled. */
export function clientProgramPhase(
  status: EngagementStatus,
): ClientProgramPhase {
  return PHASE_BY_STATUS[status];
}

/**
 * How a tab orders its programs. Past lists the most recently ended first;
 * the other tabs keep the server's status order.
 */
export function sortForTab(
  tab: ClientProgramTab,
): { sortBy: "end_date"; sortOrder: "desc" } | undefined {
  return tab === "past" ? { sortBy: "end_date", sortOrder: "desc" } : undefined;
}

/** The statuses a tab lists. `undefined` means no status filter. */
export function statusesForTab(
  tab: ClientProgramTab,
): EngagementStatus[] | undefined {
  if (tab === "all") return undefined;
  return ALL_STATUSES.filter((status) => PHASE_BY_STATUS[status] === tab);
}

export type ClientProgramTabCounts = Record<ClientProgramTab, number>;

/** Open on Past only when Current is empty and Past is not. */
export function initialClientProgramTab(
  counts: ClientProgramTabCounts,
): ClientProgramTab {
  return counts.current === 0 && counts.past > 0 ? "past" : "current";
}

/**
 * A program's main goal as the server stores it since goals are encrypted:
 * only the coach's browser, unlocked with their PIN, can read the text.
 */
export interface EncryptedMainGoal {
  id: string;
  encryptedDescription: string;
}

/** The main goal columns of a program's ranked goal row; null when it holds no ciphertext. */
export function toEncryptedMainGoal(
  id: string | null,
  encryptedDescription: string | null,
): EncryptedMainGoal | null {
  return id && encryptedDescription ? { id, encryptedDescription } : null;
}

/**
 * Whether any card shows a goal that needs the coach's PIN: one that is
 * encrypted and has no plain text from before goals were encrypted.
 */
export function hasGoalNeedingPin(
  cards: readonly {
    mainCoachingGoal: string | null;
    encryptedMainGoal?: EncryptedMainGoal | null;
  }[],
): boolean {
  return cards.some(
    (card) => !card.mainCoachingGoal && !!card.encryptedMainGoal,
  );
}

/** The highest page that still has programs; page 1 when there are none. */
export function lastClientProgramPage(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export interface ClientProgramStats {
  activeClients: number;
  needAttention: number;
  /** Invoiced minus paid, in dollars. */
  outstanding: number;
  totalPrograms: number;
}

export interface ClientProgramSummary {
  counts: ClientProgramTabCounts;
  stats: ClientProgramStats;
}

export function summarizeClientPrograms(
  programs: readonly AttentionInput[],
): ClientProgramSummary {
  const counts: ClientProgramTabCounts = {
    current: 0,
    past: 0,
    all: programs.length,
  };
  const stats: ClientProgramStats = {
    activeClients: 0,
    needAttention: 0,
    outstanding: 0,
    totalPrograms: programs.length,
  };

  for (const program of programs) {
    counts[clientProgramPhase(program.status)] += 1;
    if (program.status === EngagementStatus.ACTIVE) stats.activeClients += 1;
    if (computeAttentionSignals(program).length > 0) stats.needAttention += 1;
    stats.outstanding += program.outstandingBalance;
  }

  return { counts, stats };
}
